import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceRule, RecurrenceScope } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

export const useRecomposeEvent = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      recurrence,
      scope,
      mode,
      rebuildFuture,
    }: {
      eventId: number;
      recurrence?: RecurrenceRule | null;
      scope?: RecurrenceScope;
      mode?: "until_extend" | "until_shrink";
      rebuildFuture?: boolean;
    }) => {
      return eventsApi.recomposeEvent(eventId, {
        recurrence,
        scope,
        mode,
        rebuild_future: rebuildFuture,
      });
    },
    onSuccess: (_response, variables) => {
      queryInvalidationHelpers.invalidateEventQueries(queryClient);
      queryClient.invalidateQueries({
        queryKey: ["eventDetail", variables.eventId],
      });
    },
    onError: (error) => {
      console.error("Error recomposing event:", error);
      toast({
        title: t("common.error"),
        description: t("eventManage.eventUpdateFailed"),
        variant: "error",
      });
    },
  });

  const recomposeEvent = useCallback(
    async (
      eventId: number,
      recurrence?: RecurrenceRule | null,
      scope?: RecurrenceScope,
      mode?: "until_extend" | "until_shrink",
      rebuildFuture?: boolean,
    ) => {
      return mutation.mutateAsync({
        eventId,
        recurrence,
        scope,
        mode,
        rebuildFuture,
      });
    },
    [mutation],
  );

  return {
    recomposeEvent,
    isLoading: mutation.isPending,
    error: mutation.error ? String(mutation.error) : null,
  };
};
